import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchOrder, updateOrderStatus, createReview, fileUrl, addDeliverable, uploadFile, requestRevision, fetchGig, raiseDispute, fetchOrderDisputes } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, ArrowLeft, MessageSquare, Upload, Download, FileText, CreditCard, RefreshCcw, ShieldAlert, AlertOctagon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PaymentChooser from "@/components/PaymentChooser";
import DisputeChat from "@/components/DisputeChat";

export default function OrderDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [gig, setGig] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [revOpen, setRevOpen] = useState(false);
  const [revReason, setRevReason] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [dispOpen, setDispOpen] = useState(false);
  const [dispReason, setDispReason] = useState("");

  const load = () => fetchOrder(id).then((o) => {
    setOrder(o);
    fetchGig(o.gig_id).then(setGig).catch(() => {});
    fetchOrderDisputes(o.order_id).then(setDisputes).catch(() => setDisputes([]));
  }).catch(() => setOrder(false));
  useEffect(() => { load(); }, [id]);

  if (order === false) return <div className="p-16 text-center">Order not found.</div>;
  if (!order) return <div className="p-16 text-center">Loading…</div>;

  const isSeller = user?.user_id === order.seller_id;
  const isBuyer = user?.user_id === order.buyer_id;

  const setStatus = async (s) => {
    try { await updateOrderStatus(order.order_id, s); toast.success("Status updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const submitReview = async () => {
    try {
      await createReview({ order_id: order.order_id, rating, comment });
      toast.success("Review submitted");
      setReviewed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleDeliverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDelivering(true);
    try {
      const r = await uploadFile(file);
      await addDeliverable(order.order_id, { path: r.path, filename: file.name, note: deliveryNote });
      toast.success("Deliverable uploaded");
      setDeliveryNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setDelivering(false); e.target.value = ""; }
  };

  const retryPayment = () => setPayOpen(true);

  const submitRevision = async () => {
    if (!revReason.trim()) { toast.error("Add a reason"); return; }
    try {
      await requestRevision(order.order_id, revReason);
      toast.success("Revision requested — seller notified");
      setRevOpen(false);
      setRevReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitDispute = async () => {
    if (!dispReason.trim() || dispReason.trim().length < 3) { toast.error("Give a short reason"); return; }
    try {
      await raiseDispute(order.order_id, dispReason.trim());
      toast.success("Dispute opened — admin will review");
      setDispOpen(false);
      setDispReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openDispute = disputes.find((d) => d.status === "open");
  const canDispute = (isBuyer || isSeller) &&
    order.payment_status === "paid" &&
    !["completed", "cancelled"].includes(order.status) &&
    !openDispute;

  // Compute revision allowance from live gig package
  const pkg = gig?.packages?.find((p) => p.name === order.package_name);
  const revisionsAllowed = pkg?.revisions || 0;
  const revisionsUsed = order.revisions_used || 0;
  const revisionsLeft = Math.max(0, revisionsAllowed - revisionsUsed);

  const paymentPending = order.status === "pending_payment";
  const steps = paymentPending
    ? ["pending_payment", "in_progress", "delivered", "completed"]
    : ["in_progress", "delivered", "completed"];
  const stepIdx = steps.indexOf(order.status);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-1 mono text-xs text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="w-3 h-3"/> BACK TO DASHBOARD
      </Link>

      <div className="border border-foreground brutal-shadow-sm bg-card p-6 md:p-8 mb-6">
        <div className="flex justify-between items-start mb-6 gap-6">
          <div>
            <div className="overline text-primary mb-1">◆ ORDER · {order.order_id}</div>
            <h1 data-testid="order-title" className="text-2xl md:text-3xl font-bold tracking-tight">{order.gig_title}</h1>
            <div className="mt-2 text-sm text-muted-foreground">
              {isSeller ? "Buyer" : "Seller"}: <b className="text-foreground">{isSeller ? order.buyer_name : order.seller_name}</b>
            </div>
          </div>
          {order.gig_cover && (
            <img src={fileUrl(order.gig_cover)} className="w-28 h-20 object-cover border border-foreground/15" alt=""/>
          )}
        </div>

        {/* payment banner */}
        {paymentPending && isBuyer && (
          <div className="border-2 border-primary bg-primary/5 p-4 mb-6 flex items-center justify-between gap-4">
            <div>
              <div className="overline text-primary mb-1">◆ ACTION REQUIRED</div>
              <div className="font-medium">Complete payment to activate this order</div>
            </div>
            <button
              data-testid="retry-payment-btn"
              onClick={retryPayment}
              className="bg-primary text-primary-foreground px-5 py-2.5 mono text-xs hover:bg-primary/90 flex items-center gap-2"
            >
              <CreditCard className="w-3.5 h-3.5"/> PAY ${order.package_price}
            </button>
          </div>
        )}

        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>Complete payment</DialogTitle></DialogHeader>
            <PaymentChooser orderId={order.order_id} priceUsd={order.package_price} onPaid={() => { setPayOpen(false); load(); }}/>
          </DialogContent>
        </Dialog>

        {/* revision-pending banner (seller view) */}
        {isSeller && order.status === "in_progress" && order.revision_requests?.length > 0 && (
          <div data-testid="revision-pending-banner" className="border-2 border-primary bg-primary/5 p-4 mb-6">
            <div className="flex items-start gap-3">
              <RefreshCcw className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2}/>
              <div className="flex-1">
                <div className="overline text-primary mb-1">◆ REVISION REQUESTED</div>
                <div className="font-medium mb-1">The buyer requested a revision — please deliver again.</div>
                <div className="text-sm text-foreground/80 line-clamp-2">"{order.revision_requests[order.revision_requests.length - 1].reason}"</div>
              </div>
            </div>
          </div>
        )}

        {/* progress */}
        <div className={`grid gap-1 mb-6`} style={{gridTemplateColumns: `repeat(${steps.length}, 1fr)`}}>
          {steps.map((s, i) => (
            <div key={s} className={`p-3 text-center border ${i <= stepIdx ? "bg-primary text-primary-foreground border-primary" : "border-foreground/20 text-muted-foreground"}`}>
              <div className="overline">STEP {i+1}</div>
              <div className="mono text-xs font-semibold uppercase mt-1">{s.replace("_", " ")}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-4 border-t border-foreground/10 pt-4">
          <Info label="Package" value={order.package_name}/>
          <Info label="Price" value={`$${order.package_price}`}/>
          <Info label="Delivery" value={`${order.delivery_days} days`}/>
        </div>

        {order.requirements && (
          <div className="mt-6 border-t border-foreground/10 pt-4">
            <div className="overline mb-2">◆ Requirements</div>
            <p className="text-sm whitespace-pre-wrap">{order.requirements}</p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/messages/${isSeller ? order.buyer_id : order.seller_id}`} data-testid="order-message-btn" className="border border-foreground px-4 py-2 mono text-xs flex items-center gap-2">
            <MessageSquare className="w-3.5 h-3.5"/> MESSAGE
          </Link>
          {isBuyer && order.status === "delivered" && (
            <button data-testid="mark-completed-btn" onClick={() => setStatus("completed")} className="bg-primary text-primary-foreground px-4 py-2 mono text-xs">RELEASE & COMPLETE</button>
          )}
          {isBuyer && order.status === "delivered" && revisionsLeft > 0 && (
            <Dialog open={revOpen} onOpenChange={setRevOpen}>
              <DialogTrigger asChild>
                <button data-testid="request-revision-btn" className="border border-foreground px-4 py-2 mono text-xs hover:bg-foreground hover:text-background flex items-center gap-2">
                  <RefreshCcw className="w-3.5 h-3.5"/> REQUEST REVISION ({revisionsLeft} LEFT)
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle>Request a revision</DialogTitle></DialogHeader>
                <div className="text-sm text-muted-foreground mb-2">
                  You have <b className="text-foreground">{revisionsLeft} of {revisionsAllowed}</b> revisions remaining for this package.
                </div>
                <Textarea
                  data-testid="revision-reason"
                  value={revReason}
                  onChange={(e) => setRevReason(e.target.value)}
                  placeholder="What needs to change?"
                  rows={5}
                  className="rounded-none border-foreground/20"
                />
                <DialogFooter>
                  <button data-testid="submit-revision-btn" onClick={submitRevision} disabled={!revReason.trim()} className="bg-primary text-primary-foreground px-6 py-2.5 mono text-xs disabled:opacity-50">
                    REQUEST REVISION
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {(isSeller || isBuyer) && !["completed", "cancelled", "pending_payment"].includes(order.status) && (
            <button data-testid="cancel-order-btn" onClick={() => setStatus("cancelled")} className="border border-destructive text-destructive px-4 py-2 mono text-xs">CANCEL</button>
          )}
          {canDispute && (
            <Dialog open={dispOpen} onOpenChange={setDispOpen}>
              <DialogTrigger asChild>
                <button data-testid="raise-dispute-btn" className="border border-destructive text-destructive px-4 py-2 mono text-xs flex items-center gap-2 hover:bg-destructive hover:text-destructive-foreground">
                  <ShieldAlert className="w-3.5 h-3.5"/> RAISE DISPUTE
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle>Raise a dispute</DialogTitle></DialogHeader>
                <div className="text-sm text-muted-foreground mb-2">
                  An admin will review the order and decide on a refund. Please describe the issue clearly.
                </div>
                <Textarea
                  data-testid="dispute-reason"
                  value={dispReason}
                  onChange={(e) => setDispReason(e.target.value)}
                  placeholder="What went wrong? Include specifics (missed delivery, off-scope, poor quality, etc.)"
                  rows={5}
                  className="rounded-none border-foreground/20"
                />
                <DialogFooter>
                  <button data-testid="submit-dispute-btn" onClick={submitDispute} disabled={dispReason.trim().length < 3} className="bg-destructive text-destructive-foreground px-6 py-2.5 mono text-xs disabled:opacity-50">
                    OPEN DISPUTE
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Disputes */}
      {disputes.length > 0 && (
        <div data-testid="disputes-section" className="border border-destructive/40 bg-destructive/5 p-6 mb-6">
          <div className="flex items-center gap-2 overline text-destructive mb-4">
            <AlertOctagon className="w-4 h-4"/> ◆ DISPUTES
          </div>
          <div className="space-y-4">
            {disputes.map((d) => (
              <div key={d.dispute_id} data-testid={`dispute-${d.dispute_id}`} className="border-l-2 border-destructive pl-4 py-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="mono text-[10px] text-muted-foreground uppercase">
                    Opened by {d.raised_by} · {new Date(d.created_at).toLocaleString()}
                  </div>
                  <span className={`mono text-[10px] px-2 py-0.5 border ${d.status === "open" ? "border-destructive text-destructive" : d.status === "resolved_refund" ? "border-primary text-primary" : "border-foreground/40"}`}>
                    {d.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap mb-2">"{d.reason}"</div>
                {d.admin_note && (
                  <div className="mt-2 border-t border-foreground/10 pt-2 text-xs">
                    <span className="mono text-muted-foreground">ADMIN NOTE: </span>{d.admin_note}
                  </div>
                )}
                <DisputeChat dispute={d} testIdPrefix={`order-disp-chat-${d.dispute_id}`}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revisions history */}
      {order.revision_requests?.length > 0 && (
        <div className="border border-foreground/15 bg-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-primary">◆ REVISION HISTORY</div>
            <div className="mono text-xs text-muted-foreground">{revisionsUsed}/{revisionsAllowed} used</div>
          </div>
          <div className="space-y-3">
            {order.revision_requests.map((r, i) => (
              <div key={r.revision_id} data-testid={`revision-${i}`} className="border-l-2 border-primary pl-4 py-1">
                <div className="mono text-[10px] text-muted-foreground mb-1">{new Date(r.requested_at).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap">{r.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deliverables */}
      {(order.status === "in_progress" || order.status === "delivered" || order.status === "completed") && (
        <div className="border border-foreground/15 bg-card p-6 mb-6">
          <div className="overline text-primary mb-4">◆ DELIVERABLES</div>
          {(!order.deliverables || order.deliverables.length === 0) ? (
            <div className="text-sm text-muted-foreground mb-4">No files delivered yet.</div>
          ) : (
            <div className="space-y-3 mb-4">
              {order.deliverables.map((d) => (
                <div key={d.deliverable_id} data-testid={`deliverable-${d.deliverable_id}`} className="border border-foreground/10 p-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={1.5}/>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{d.filename}</div>
                      {d.note && <p className="text-sm text-muted-foreground mt-1">{d.note}</p>}
                      <div className="mono text-xs text-muted-foreground mt-1">{new Date(d.uploaded_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <a
                    href={fileUrl(d.path)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-${d.deliverable_id}`}
                    className="border border-foreground px-3 py-1.5 mono text-xs flex items-center gap-1.5 hover:bg-foreground hover:text-background"
                  >
                    <Download className="w-3 h-3"/> DOWNLOAD
                  </a>
                </div>
              ))}
            </div>
          )}

          {isSeller && (order.status === "in_progress" || order.status === "delivered") && (
            <div className="border-t border-foreground/10 pt-4">
              <div className="overline mb-2">◆ Upload new delivery</div>
              <Textarea
                data-testid="delivery-note"
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="Optional note for the buyer"
                rows={2}
                className="rounded-none border-foreground/20 mb-3"
              />
              <label className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 mono text-xs cursor-pointer hover:bg-zinc-800">
                <Upload className="w-3.5 h-3.5"/> {delivering ? "UPLOADING…" : "UPLOAD FILE & DELIVER"}
                <input data-testid="deliver-file-input" type="file" hidden onChange={handleDeliverUpload} disabled={delivering}/>
              </label>
              <p className="text-xs text-muted-foreground mono mt-2">Uploading marks the order as DELIVERED.</p>
            </div>
          )}
        </div>
      )}

      {isBuyer && order.status === "completed" && !reviewed && (
        <div className="border border-foreground/15 bg-card p-6">
          <div className="overline text-primary mb-3">◆ LEAVE A REVIEW</div>
          <div className="flex gap-1 mb-4">
            {[1,2,3,4,5].map((n) => (
              <button
                key={n}
                data-testid={`rating-star-${n}`}
                onClick={() => setRating(n)}
                className="p-1"
              >
                <Star className={`w-7 h-7 ${n <= rating ? "fill-primary text-primary" : "text-muted-foreground"}`}/>
              </button>
            ))}
          </div>
          <Textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience..."
            rows={4}
            className="rounded-none border-foreground/20 mb-4"
          />
          <button
            data-testid="submit-review-btn"
            onClick={submitReview}
            disabled={!comment.trim()}
            className="bg-primary text-primary-foreground px-5 py-2.5 mono text-xs disabled:opacity-50"
          >SUBMIT REVIEW</button>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mono font-semibold mt-1">{value}</div>
    </div>
  );
}
